import { memo, useState } from 'react';
import { Download, Trophy, Clock, Eye, Calendar, Activity, Star } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { APOGEE_ACTIVITY_WINDOWS, ApogeeActivityWindow, PackRow } from '../../types/apogee';
import { SkeletonLoader } from '../SkeletonLoader';
import { StatTile } from '../ui/StatTile';
import { cn } from '../../utils/cn';

interface PackStatsCardsProps {
  pack: PackRow | null;
  isLoading?: boolean;
  className?: string;
}

const formatNumber = (n: number | undefined | null): string =>
  n == null || Number.isNaN(n) ? '—' : n.toLocaleString();

export const PackStatsCards = memo(({ pack, isLoading, className }: PackStatsCardsProps) => {
  const hasFirstRelease = !!pack?.first_release_at;
  const [activityWindow, setActivityWindow] = useState<ApogeeActivityWindow>('30d');
  const actWin = pack?.apogee_activity?.windows?.[activityWindow];
  const cfFeat = pack?.curseforge_featured;

  return (
    <div className={cn(
      'grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6',
      className,
    )}>
      <StatTile
        label="Total Downloads"
        value={pack ? formatNumber(pack.download_count) : '—'}
        icon={Download}
        tone="accent"
        loading={isLoading}
      />
      <StatTile
        label="Popularity Rank"
        value={
          pack
            ? typeof pack.popularity_rank === 'number'
              ? `#${pack.popularity_rank}`
              : 'No rollup'
            : '—'
        }
        icon={Trophy}
        tone="amber"
        loading={isLoading}
      />
      {hasFirstRelease && (
        <StatTile
          label="First Release"
          value={formatDistanceToNow(new Date(pack!.first_release_at!), { addSuffix: true })}
          hint={format(new Date(pack!.first_release_at!), 'MMM d, yyyy')}
          icon={Calendar}
          tone="purple"
          loading={isLoading}
        />
      )}
      <StatTile
        label="First Seen"
        value={pack ? formatDistanceToNow(new Date(pack.first_seen_at), { addSuffix: true }) : '—'}
        hint={pack ? format(new Date(pack.first_seen_at), 'MMM d, yyyy') : undefined}
        icon={Eye}
        tone="info"
        loading={isLoading}
      />
      <StatTile
        label="Last Updated"
        value={pack?.last_updated ? formatDistanceToNow(new Date(pack.last_updated), { addSuffix: true }) : '—'}
        hint={pack?.last_updated ? format(new Date(pack.last_updated), 'MMM d, yyyy') : undefined}
        icon={Clock}
        tone="success"
        loading={isLoading}
      />
      {/* Apogee activity — same shape as StatTile, with a window picker in place of the icon */}
      <div className="glass-card p-4 flex items-start justify-between gap-3">
        {isLoading ? (
          <div className="flex-1 min-w-0 space-y-2">
            <SkeletonLoader className="h-3 w-20" />
            <SkeletonLoader className="h-7 w-16" />
          </div>
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <div className="eyebrow truncate">Apogee activity</div>
              <div className="mt-1 text-2xl font-semibold tracking-tight tabular-nums text-white truncate">
                {actWin ? actWin.score.toFixed(1) : '—'}
              </div>
              <div className="mt-1 text-xs text-glass-500 tabular-nums">
                {actWin ? `Rank #${actWin.rank.toLocaleString()}` : 'No score yet'}
                {pack?.apogee_activity?.computed_at && (
                  <span className="block mt-0.5 truncate">
                    Computed {formatDistanceToNow(new Date(pack.apogee_activity.computed_at), { addSuffix: true })}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <span className="icon-tile icon-tile-purple">
                <Activity className="w-4 h-4" />
              </span>
              <select
                className="glass-select text-xs min-h-0 py-0.5 px-1.5 max-w-[4.5rem]"
                value={activityWindow}
                onChange={(e) => setActivityWindow(e.target.value as ApogeeActivityWindow)}
                title="Activity lookback window"
                aria-label="Apogee activity window"
              >
                {APOGEE_ACTIVITY_WINDOWS.map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>
      <StatTile
        label="CurseForge featured"
        value={
          pack
            ? cfFeat?.currently_featured
              ? 'On carousel'
              : cfFeat
                ? 'Not featured'
                : '—'
            : '—'
        }
        hint={
          pack && cfFeat?.currently_featured && cfFeat.since
            ? `Since ${formatDistanceToNow(new Date(cfFeat.since), { addSuffix: true })}`
            : pack && cfFeat && !cfFeat.currently_featured && cfFeat.last_seen_at
              ? `Last on carousel ${formatDistanceToNow(new Date(cfFeat.last_seen_at), { addSuffix: true })}`
              : pack && cfFeat?.cf_mod_id != null
                ? `CF project ${cfFeat.cf_mod_id}`
                : undefined
        }
        icon={Star}
        tone="amber"
        loading={isLoading}
      />
    </div>
  );
});

PackStatsCards.displayName = 'PackStatsCards';
